<script lang="ts" setup>
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';

import Swal from 'sweetalert2';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { ElMessage } from 'element-plus';
import QrcodeVue from 'qrcode.vue';
import { QuillEditor } from '@vueup/vue-quill';
import sharedOrg from '@/views/manage-users-and-organizations/shared';
import shared from '@/views/manage-content/media/shared';
import fileUpload from '@/shared/upload';

import '@vueup/vue-quill/dist/vue-quill.snow.css';
import '@/assets/styles/qill.scss';
import StickerContent from './components/StickerContent.vue';
import DefaultSticker from '@/views/manage-content/media/add-sticker/components/DefaultSticker.vue';
import CampaignSticker from '@/views/manage-content/media/add-sticker/components/CampaignSticker.vue';
import TicketSticker from '@/views/manage-content/media/add-sticker/components/TicketSticker.vue';
import SweepstakesSticker from './components/SweepstakesSticker.vue';
import router from '@/router';
import BreadcrumbActions from '@/views/manage-content/add-item/BreadcrumbActions.vue';
import Insights from '@/views/manage-content/add-item/Insights.vue';
import { uploadDefaultStickerMap, uploadTicketStickerMap } from '@/shared/upload-sticker-model';
import { resizeAndConvertToPNG } from '@/shared/utility-functions';

const store = useStore();
const route = useRoute();
const isEditPage = ref(false);

const { uploadStatus, handleUploadRequest } = fileUpload('stickers', 'Sticker');

// Define interfaces first
interface TagData {
  value: string;
  label: string;
}
interface CauseData {
  value: string;
  label: string;
}

interface Winner {
  id: string;
  stickerId: string;
  entityId: string;
  participantName: string;
  participantAvatar: string;
  participantEmail: string;
  eligible: boolean;
  dateCreated: string;
  dateUpdated: string;
}

// Define ref variables
const winnerName = ref<string | null>(null);
const errors = ref<string[]>([]);
const hasAttemptedSubmit = ref(false);
const winners = ref<Winner[] | null>(null);
const selectedWinners = ref<Winner[]>([]);
const clickCount = ref(0);
const isTextVisible = ref(false);
const isStickerSold = ref(false);
const stickerUrl = ref('');
const fetchError = ref<string | null>(null);
const isStripeOnboarded = ref(false);
const isUploading = ref(false);

const item = store.getters['sticker/GET_STICKER'];

// Computed property for sticker deep link
const stickerDeepLink = computed(() => `/sticker-details/${item.id}`);

// Copy sticker deep link function
const copyStickerDeepLink = async () => {
  try {
    await navigator.clipboard.writeText(stickerDeepLink.value);
    ElMessage({
      message: 'Sticker deep link copied to clipboard!',
      type: 'success',
    });
  } catch (error) {
    console.error('Failed to copy sticker deep link:', error);
    ElMessage({
      message: 'Failed to copy deep link',
      type: 'error',
    });
  }
};

const {
  updateSticker,
  sendLoserEmails,
  getBullhornsAsTags,
  getcausesAsDropdown,
  getSticker,
  dateAndTime,
  drawWinner,
  getWinners,
  findStickerInsights,
} = shared();
const { updateOrgSticker, getOrganization, doGetStripeAuthorizeUrl } = sharedOrg();

const stickerType = ref('default'); // default, campaign, ticket, sweepstakes

const isAdmin = computed(() => store.getters['users/isAdmin']);
const isOrgAdmin = computed(() => store.getters['users/isOrgAdmin']);
const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);

// Helper function to determine if a sticker type is paid
const isPaidStickerType = (type: string, stickerItem: any): boolean => {
  switch (type) {
    case 'default':
      return stickerItem.cost > 0;

    case 'campaign':
      // Campaign stickers are paid if they have quickCosts or a cost > 0
      return stickerItem.cost > 0
             || (stickerItem.quickCosts && stickerItem.quickCosts.some((cost: number) => cost > 0));

    case 'ticket': {
      // Ticket stickers are paid if they have any main variants
      // or ticket tier add-ons with cost > 0
      const hasPaidVariant = stickerItem.varients
        && stickerItem.varients.some((v: any) => !v.isFree && v.cost > 0);

      // Check for paid donation add-ons
      const hasPaidDonation = stickerItem.addOns
        && stickerItem.addOns.some((a: any) => {
          if (a.type !== 'Donation') return false;
          if (a.donationPricing === 'fixed') {
            return a.donationFixed > 0;
          }
          if (a.donationPricing === 'suggested') {
            return a.donationSuggested && a.donationSuggested.some((c: number) => c > 0);
          }
          return false;
        });

      // Check for paid glow add-ons
      const hasPaidGlow = stickerItem.addOns
        && stickerItem.addOns.some((a: any) => (
          a.type === 'Glow' && !a.glowIsFree && a.glowPrice > 0
        ));

      return !!(hasPaidVariant || hasPaidDonation || hasPaidGlow);
    }

    case 'sweepstakes':
      // Sweepstakes are paid if they have entry costs
      return stickerItem.sweepstakesSticker?.entryCost
             && stickerItem.sweepstakesSticker.entryCost.some((cost: number) => cost > 0);

    default:
      return false;
  }
};

const isPaidSticker = computed(() => isPaidStickerType(stickerType.value, item));

const targetOrgId = computed(() => {
  if (selectedOrganization.value) {
    return selectedOrganization.value.id;
  }
  if (item && item.entityId) {
    return item.entityId;
  }
  return null;
});

const showStripeWarning = computed(() => {
  if (!targetOrgId.value || isStripeOnboarded.value) {
    return false;
  }
  
  // Campaigns never require Stripe
  if (stickerType.value === 'campaign') {
    return false;
  }
  
  // Sweepstakes never require Stripe
  if (stickerType.value === 'sweepstakes') {
    return false;
  }
  
  // Tickets only require Stripe if they are paid
  if (stickerType.value === 'ticket') {
    return isPaidSticker.value;
  }
  
  // Default stickers only require Stripe if paid
  if (stickerType.value === 'default') {
    return isPaidSticker.value;
  }
  
  return false;
});

watch(targetOrgId, async (newOrgId) => {
  if (newOrgId) {
    try {
      const organization = await getOrganization(newOrgId);
      isStripeOnboarded.value = organization?.stripe_onboarded || false;
    } catch (error) {
      console.error('[AddSticker] Failed to fetch organization onboarding status:', error);
      isStripeOnboarded.value = false;
    }
  } else {
    isStripeOnboarded.value = false;
  }
}, { immediate: true });

const isSweepstakeDisabled = computed(() => {
  // Only disable if someone has purchased, regardless of Active/Archived status
  if (isStickerSold.value) {
    return true;
  }
  return false;
});

const slugTip = 'A slug is a URL-friendly version of your sticker name. It creates clean, memorable URLs like '
  + '"spotlight.xyz/web/sticker/your-sticker-name". Slugs improve SEO, make sharing easier, and provide a '
  + 'professional appearance. Use lowercase letters, numbers, and hyphens only.';

const sanitizeSlug = () => {
  item.slug = item.slug.replace(/[^a-z0-9-]/g, '').toLowerCase();
};

// Helper functions defined before they are used
const getSweepstakesStickerProperties = (itemParam: any) => ({
  id: itemParam.id,
  title: itemParam.title,
  slug: itemParam.slug,
  description: itemParam.description,
  url: itemParam.url,
  tags: itemParam.tags,
  cost: itemParam.cost,
  campaignType: itemParam.campaignType,
  causes: itemParam.causes,
  maxQuantity: itemParam.maxQuantity,
  maxDonation: itemParam.maxDonation,
  donationMultiplier: itemParam.donationMultiplier,
  impact: itemParam.impact,
  startDate: itemParam.startDate,
  endDate: itemParam.endDate,
  waiverURL: itemParam.waiverURL,
  varients: null,
  quickCosts: [],
  glow: itemParam.glow ? {
    glowStartDate: itemParam.glow.glowStartDate,
    glowEndDate: itemParam.glow.glowEndDate,
    glowColor: itemParam.glow.glowColor,
    glowStartDateTimeZone: itemParam.glow.glowStartDateTimeZone,
    glowEndDateTimeZone: itemParam.glow.glowEndDateTimeZone,
  } : null,
  location: itemParam.location,
  isFeatured: itemParam.isFeatured,
  isCausesSearchAllowed: itemParam.isCausesSearchAllowed,
  confirmationEmail: itemParam.confirmationEmail,
  reminderEmail: itemParam.reminderEmail,
  hasOptions: itemParam.hasOptions,
  sweepstakesSticker: {
    aboutTheCause: itemParam.sweepstakesSticker.aboutTheCause,
    numberOfPrizesAndWinners: itemParam.sweepstakesSticker.numberOfPrizesAndWinners,
    prizes: itemParam.sweepstakesSticker.prizes,
    nonProfitBeneficiary: itemParam.sweepstakesSticker.nonProfitBeneficiary,
    drawingDateTime: itemParam.sweepstakesSticker.drawingDateTime,
    drawingDateTimeTimezone: itemParam.sweepstakesSticker.drawingDateTimeTimezone,
    experienceDateTime: itemParam.sweepstakesSticker.experienceDateTime,
    experienceDateTimeTimezone: itemParam.sweepstakesSticker.experienceDateTimeTimezone,
    approximateRetailValue: itemParam.sweepstakesSticker.approximateRetailValue,
    sweepstakesConfirmation: itemParam.sweepstakesSticker.sweepstakesConfirmation,
    sweepstakesWinnerMessage: itemParam.sweepstakesSticker.sweepstakesWinnerMessage,
    sweepstakesLoserMessage: itemParam.sweepstakesSticker.sweepstakesLoserMessage,
    checkoutPageLegal: itemParam.sweepstakesSticker.checkoutPageLegal,
    entries: itemParam.sweepstakesSticker.entries,
    entryCost: itemParam.sweepstakesSticker.entryCost,
  },
  startDateTimeZone: itemParam.startDateTimeZone || null,
  endDateTimeZone: itemParam.endDateTimeZone || null,
  eventDateTimeZone: itemParam.eventDateTimeZone || null,
});

const getStickerProperties = (itemParam: any) => ({
  id: itemParam.id,
  title: itemParam.title,
  slug: itemParam.slug,
  description: itemParam.description,
  url: itemParam.url,
  tags: itemParam.tags,
  cost: itemParam.cost,
  campaignType: itemParam.campaignType,
  causes: itemParam.causes,
  maxQuantity: itemParam.maxQuantity,
  maxDonation: itemParam.maxDonation,
  donationMultiplier: itemParam.donationMultiplier,
  impact: itemParam.impact,
  startDate: itemParam.startDate,
  endDate: itemParam.endDate,
  waiverURL: itemParam.waiverURL,
  location: itemParam.location,
  varients: {
    id: itemParam.id,
    cost: itemParam.cost,
    title: itemParam.title,
    sortOrder: itemParam.sortOrder,
    stickerId: itemParam.id,
    description: itemParam.description,
    isActive: itemParam.isActive,
    createdAt: itemParam.createdAt,
    updatedAt: itemParam.updatedAt,
  },
  glow: itemParam.glow,
  quickCosts: itemParam.quickCosts,
  sweepstakesSticker: null,
  startDateTimeZone: itemParam.startDateTimeZone || null,
  endDateTimeZone: itemParam.endDateTimeZone || null,
  eventDateTimeZone: itemParam.eventDateTimeZone || null,
});

const populateItem = async (itemParam: any) => {
  console.log('[AddSticker][populateItem] raw varients from server:', JSON.stringify((itemParam.varients || []).map((v) => ({
    id: v.id,
    title: v.title,
    whatsIncluded: v.whatsIncluded,
    selectionOptions: v.selectionOptions,
    isActive: v.isActive,
  }))));
  itemParam.tags = (typeof itemParam.tags === 'string' && itemParam.tags !== '') ? itemParam.tags.split(' |=| ') : [];
  itemParam.causes = (typeof itemParam.causes === 'string' && itemParam.causes !== '') ? itemParam.causes.split(' |=| ') : [];
  [itemParam.postDate, itemParam.postTime] = dateAndTime(itemParam.createdAt);
  itemParam.varients = itemParam.varients.filter((x: any) => x.isActive === true);
  console.log('[AddSticker][populateItem] varients after isActive filter:', JSON.stringify((itemParam.varients || []).map((v) => ({
    id: v.id,
    title: v.title,
    whatsIncluded: v.whatsIncluded,
    selectionOptions: v.selectionOptions,
  }))));

  itemParam.glow = {
    glowStartDate: itemParam.glow?.glowStartDate,
    glowEndDate: itemParam.glow?.glowEndDate,
    glowColor: itemParam.glow?.glowColor,
    glowStartDateTimeZone: itemParam.glow?.glowStartDateTimeZone,
    glowEndDateTimeZone: itemParam.glow?.glowEndDateTimeZone,
  };

  await store.dispatch('sticker/SET_STICKER', {
    endDate: itemParam.endDate,
    startDate: itemParam.startDate,
  });

  switch (itemParam.campaignType) {
    case 'Ticket':
      stickerType.value = 'ticket';
      break;
    case 'campaign':
      stickerType.value = 'campaign';
      break;
    case 'Campaign':
      stickerType.value = 'campaign';
      break;
    case 'Sweepstakes':
      stickerType.value = 'sweepstakes';
      break;
    case null:
      stickerType.value = 'default';
      break;
    default: stickerType.value = 'default';
  }
};

const isLoading = ref(false);

const fetchSticker = async () => {
  isLoading.value = true;
  fetchError.value = null;
  try {
    const fetchedItem = await getSticker(route.params.id);
    await populateItem(fetchedItem);
    await store.dispatch('sticker/SET_STICKER', fetchedItem);
    if (fetchedItem.id) {
      // Use slug if available, fallback to ID
      const urlIdentifier = fetchedItem.slug || fetchedItem.id;
      stickerUrl.value = `${import.meta.env.VITE_WEB_BASE_URL}/web/sticker/${urlIdentifier}`;
    }
  } catch (error: any) {
    console.error('[AddSticker] fetchSticker error:', error);
    fetchError.value = error?.message || 'Failed to load sticker. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

// Add this function to update URL when slug changes
const updateStickerUrl = () => {
  if (item.id) {
    const urlIdentifier = item.slug || item.id;
    stickerUrl.value = `${import.meta.env.VITE_WEB_BASE_URL}/web/sticker/${urlIdentifier}`;
  }
};

// Watch for slug changes and update URL
watch(() => item.slug, () => {
  updateStickerUrl();
});

const getStickerInsights = async () => {
  if (!route.params.id) {
    console.warn('Cannot fetch sticker insights: stickerId is missing');
    return;
  }
  const mStickerInsights = await findStickerInsights(route.params.id);
  if (mStickerInsights.purchased) {
    isStickerSold.value = true;
  }
};

const updateAdminOrOrgAdminSticker = async (sticker: any) => {
  // Remove organization validation requirement - organization is optional
  // Only validate organization if user is org admin and we're not in edit mode
  if (!isEditPage.value && isOrgAdmin.value && !selectedOrganization.value) {
    errors.value = errors.value.filter((error) => error !== 'Organization');
    errors.value.push('Organization');

    setTimeout(() => {
      const errorElement = document.querySelector('.alert.alert-danger');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    return null;
  }

  // If organization is selected, attach the sticker to that organization
  if (selectedOrganization.value) {
    return await updateOrgSticker(selectedOrganization.value.id, sticker);
  }

  // If no organization is selected, create as Spotlight sticker (admin only)
  return await updateSticker(sticker);
};

const validationErrors = computed(() => {
  const cur = store.getters['sticker/GET_STICKER'];
  const errs: string[] = [];

  // ── Shared across all types ──────────────────────────────────────
  if (!cur.title?.length) errs.push('Title');
  if (!cur.slug?.length) errs.push('Slug');
  if (!cur.url?.length) errs.push('Image');

  const descContent = cur.description || '';
  if (descContent.replace(/<[^>]*>/g, '').trim() === '') errs.push('Details');

  // ── Default ──────────────────────────────────────────────────────
  if (stickerType.value === 'default') {
    if (!cur.tags?.length) errs.push('Tags');
  }

  // ── Campaign ─────────────────────────────────────────────────────
  if (stickerType.value === 'campaign') {
    if (!cur.tags?.length) errs.push('Tags');
    if (!cur.causes?.length) errs.push('Non-Profit Suggestions');
    if (!cur.startDateTimeZone) errs.push('Timezone');
    if (!cur.startDate) errs.push('Start Date & Time');
    if (!cur.endDate) errs.push('End Date & Time');
  }

  // ── Ticket (fields per BE docs) ──────────────────────────────────
  // Required: title, url, description (above) + eventDate, eventDateTimeZone,
  //           at least one tier, and location/virtualLink per locationType.
  if (stickerType.value === 'ticket') {
    if (!cur.eventDate) errs.push('Event Date & Time');
    if (!cur.eventDateTimeZone) errs.push('Event Timezone');
    if (cur.locationType === 'physical' && !cur.location) errs.push('Location (Address)');
    if (cur.locationType === 'virtual' && !cur.virtualLink) errs.push('Virtual Link');
    if (!cur.varients?.length) errs.push('Ticket Tier');

    const donationAddOns = (cur.addOns || []).filter((a: any) => a.type === 'Donation');
    const hasDonationWithoutCharity = donationAddOns.some(
      (a: any) => !a.donationCauses?.some((c: string) => c && c.trim().length > 0),
    );
    if (hasDonationWithoutCharity) errs.push('Donation Add-On Charity Beneficiary');
  }

  // ── Sweepstakes ──────────────────────────────────────────────────
  if (stickerType.value === 'sweepstakes') {
    if (!cur.sweepstakesSticker?.numberOfPrizesAndWinners
        || cur.sweepstakesSticker.numberOfPrizesAndWinners <= 0) {
      errs.push('Number of Prizes and Winners');
    }
    if (!cur.sweepstakesSticker?.drawingDateTime) errs.push('Drawing Date & Time');
    if (!cur.startDateTimeZone) errs.push('Timezone');
    if (!cur.startDate) errs.push('Start Date & Time');
    if (!cur.endDate) errs.push('End Date & Time');

    const { entries, entryCost } = cur.sweepstakesSticker || {};
    const hasValidEntries = Array.isArray(entries)
      && entries.some((e) => typeof e === 'number' && e > 0);
    const hasValidCosts = Array.isArray(entryCost)
      && entryCost.some((c) => typeof c === 'number' && c >= 0);
    if (!hasValidEntries || !hasValidCosts) errs.push('Donation Tiers');
  }

  return errs;
});

const redirectToStickersPage = async () => {
  // If organization is selected, redirect to organization stickers page
  if (selectedOrganization.value) {
    if (isOrgAdmin.value) {
      await router.push(`/manage/orgs/${selectedOrganization.value.id}/stickers`);
      return;
    }
  }

  // If no organization is selected or user is admin, redirect to main stickers page
  await router.push('/manage-content/media/stickers');
};

const uploadDefaultSticker = async () => {
  if (validationErrors.value.length) {
    window.scrollTo(0, 0);
    return;
  }

  const updatedItem = uploadDefaultStickerMap(item);

  try {
    await updateAdminOrOrgAdminSticker(updatedItem);
    await redirectToStickersPage();
  } catch (error) {
    throw error;
  }
};

const uploadCampaignSticker = async () => {
  if (validationErrors.value.length) {
    window.scrollTo(0, 0);
    return;
  }
  const updatedItem = item;

  const cQuickCosts: number[] = [];
  const qc = item.quickCosts || [];
  qc.forEach((q) => {
    if (typeof q === 'number') {
      cQuickCosts.push(q);
    }
  });

  updatedItem.quickCosts = cQuickCosts;
  updatedItem.cost = parseFloat(parseFloat(updatedItem.cost).toFixed(2));
  if (Array.isArray(updatedItem.tags)) {
    updatedItem.tags = updatedItem.tags.join(' |=| ');
  } else if (typeof updatedItem.tags === 'string') {
    updatedItem.tags = updatedItem.tags.split(',').join(' |=| ');
  }

  if (Array.isArray(updatedItem.causes)) {
    updatedItem.causes = updatedItem.causes.join(' |=| ');
  } else if (typeof updatedItem.causes === 'string') {
    updatedItem.causes = updatedItem.causes.split(',').join(' |=| ');
  }

  item.campaignType = 'Campaign';
  updatedItem.varients = [];
  updatedItem.sweepstakesSticker = null;
  const stickerProps = getStickerProperties(updatedItem);
  try {
    await store.dispatch('sticker/SET_STICKER', stickerProps);
    await updateAdminOrOrgAdminSticker(stickerProps);
    await redirectToStickersPage();
  } catch (error) {
    throw error;
  }
};

const uploadTicketSticker = async () => {
  if (validationErrors.value.length) {
    window.scrollTo(0, 0);
    return;
  }

  const updatedItem = uploadTicketStickerMap(item);
  console.log('[AddSticker][uploadTicketSticker] final payload varients:', JSON.stringify((updatedItem.varients || []).map((v) => ({
    id: v.id,
    title: v.title,
    whatsIncluded: v.whatsIncluded,
    selectionOptions: v.selectionOptions,
  }))));
  if (updatedItem.varients?.length) {
    updatedItem.cost = 0;
  }
  try {
    await store.dispatch('sticker/SET_STICKER', updatedItem);
    const saveResponse = await updateAdminOrOrgAdminSticker(updatedItem);
    console.log('[AddSticker][uploadTicketSticker] server response varients:', JSON.stringify((saveResponse?.varients || []).map((v) => ({
      id: v.id,
      title: v.title,
      whatsIncluded: v.whatsIncluded,
      selectionOptions: v.selectionOptions,
    }))));
    await redirectToStickersPage();
  } catch (error) {
    throw error;
  }
};

const onSaveDraft = async () => {
  if (isUploading.value) return;
  hasAttemptedSubmit.value = true;

  if (showStripeWarning.value) {
    const message = stickerType.value === 'ticket' 
      ? 'A connected Stripe account is required to save paid tickets. Please connect Stripe first.'
      : 'A connected Stripe account is required to save paid stickers. Please connect Stripe first.';
    ElMessage.error({
      message,
      duration: 5000,
    });
    return;
  }

  isUploading.value = true;
  try {
    await uploadTicketSticker();
  } catch (error) {
    console.error('Error saving draft:', error);
    ElMessage.error({ message: 'Failed to save draft. Please try again.', duration: 3000 });
  } finally {
    isUploading.value = false;
  }
};

const onPublishEvent = async () => {
  if (isUploading.value) return;
  hasAttemptedSubmit.value = true;

  if (showStripeWarning.value) {
    const message = stickerType.value === 'ticket'
      ? 'A connected Stripe account is required to publish paid tickets. Please connect Stripe first.'
      : 'A connected Stripe account is required to publish paid stickers. Please connect Stripe first.';
    ElMessage.error({
      message,
      duration: 5000,
    });
    return;
  }

  isUploading.value = true;
  try {
    await store.dispatch('sticker/SET_STICKER', { status: 'Active' });
    await uploadTicketSticker();
  } catch (error) {
    console.error('Error publishing event:', error);
    ElMessage.error({ message: 'Failed to publish event. Please try again.', duration: 3000 });
  } finally {
    isUploading.value = false;
  }
};

const uploadSweepstakesSticker = async () => {
  if (validationErrors.value.length) {
    window.scrollTo(0, 0);
    return;
  }

  const addItem = { ...item };
  addItem.cost = 0;
  addItem.tags = null;
  addItem.campaignType = 'Sweepstakes';
  addItem.isFeatured = false;
  addItem.IsCausesSearchAllowed = false;
  addItem.varients = [];

  if (addItem.sweepstakesSticker.numberOfPrizesAndWinners === null || addItem.sweepstakesSticker.numberOfPrizesAndWinners === '') {
    addItem.sweepstakesSticker.numberOfPrizesAndWinners = 1;
  }

  if (Array.isArray(item.causes)) {
    addItem.causes = item.causes.join(' |=| ');
  } else if (typeof item.causes === 'string') {
    addItem.causes = item.causes.split(',').join(' |=| ');
  } else {
    addItem.causes = '';
  }

  addItem.sweepstakesSticker.nonProfitBeneficiary = addItem.causes;

  const updatedItem = getSweepstakesStickerProperties(addItem);

  try {
    await store.dispatch('sticker/SET_STICKER', updatedItem);
    await updateAdminOrOrgAdminSticker(updatedItem);
    await redirectToStickersPage();
  } catch (error) {
    throw error;
  }
};

const onUpload = async () => {
  hasAttemptedSubmit.value = true;
  if (stickerType.value === 'default') {
    await uploadDefaultSticker();
  } else if (stickerType.value === 'campaign') {
    await uploadCampaignSticker();
  } else if (stickerType.value === 'ticket') {
    await uploadTicketSticker();
  } else if (stickerType.value === 'sweepstakes') {
    await uploadSweepstakesSticker();
  }
};

const onDraw = async () => {
  errors.value = [];
  const sweepstakesStickerId = item.id;
  const participantIds = selectedWinners.value.map((winner) => winner.entityId);

  if (participantIds.length > 0) {
    try {
      return await drawWinner(sweepstakesStickerId, participantIds);
    } catch (error) {
      console.error('Error drawing winners:', error);
      ElMessage.error({
        message: 'Failed to draw winners. Please try again.',
        duration: 3000,
      });
    }
  } else {
    ElMessage.warning({
      message: 'Please select at least one participant to draw a winner.',
      duration: 3000,
    });
  }
  return null;
};

const getWinnersList = async () => {
  try {
    const sweepstakesStickerId = item.id;
    const response = await getWinners(sweepstakesStickerId);
    winners.value = response;
    if (winners.value) {
      winnerName.value = winners.value.map((winner) => winner.participantName).join(', ');
    } else {
      winnerName.value = '';
    }
  } catch (error) {
    console.error('Error fetching winners:', error);
  }
};

function confirmWinners() {
  Swal.fire({
    title: '<h3 style="font-size: 18px;">Are you sure?</h3>',
    html: '<p style="font-size: 14px;">Do you want to confirm winners?</p>',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#1EBEAF',
    cancelButtonColor: '#f43838',
    confirmButtonText: 'Yes',
    cancelButtonText: 'Cancel',
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await sendLoserEmails(item.id);
        window.location.reload();
      } catch (error) {
        console.error('Error sending loser emails:', error);
      }
    }
  });
}

const handleButtonClick = async () => {
  clickCount.value += 1;

  if (clickCount.value === 1) {
    isTextVisible.value = true;
    await getWinnersList();
  }

  if (clickCount.value === 2) {
    await onDraw();
    isTextVisible.value = false;
    clickCount.value = 0;
  }
};

const handleClose = async () => {
  isTextVisible.value = false;
  clickCount.value = 0;
  await getWinnersList();
};

const onAddImage = async (payload: any) => {
  try {
    if (payload.file) {
      const compressedFile = await resizeAndConvertToPNG(payload.file);

      const compressedPayload = {
        ...payload,
        file: compressedFile,
      };

      await handleUploadRequest(compressedPayload);

      if (uploadStatus.status === 'ok') {
        item.url = uploadStatus.resourceUrl;

        if (uploadStatus.thumbnailUrl) {
          item.thumbnailUrl = uploadStatus.thumbnailUrl;
        }
      }
    } else {
      await handleUploadRequest(payload);
      if (uploadStatus.status === 'ok') {
        item.url = uploadStatus.resourceUrl;
        if (uploadStatus.thumbnailUrl) {
          item.thumbnailUrl = uploadStatus.thumbnailUrl;
        }
      }
    }
  } catch (error) {
    console.error('Error processing image:', error);
    await handleUploadRequest(payload);
    if (uploadStatus.status === 'ok') {
      item.url = uploadStatus.resourceUrl;
      if (uploadStatus.thumbnailUrl) {
        item.thumbnailUrl = uploadStatus.thumbnailUrl;
      }
    }
  }
};

const tagsList = ref<TagData[]>([]);
const tagsArray = ref<string[]>([]);
async function getBullhornsAsTagsList() {
  const bullhornTags = await getBullhornsAsTags();

  bullhornTags.forEach((element: any) => {
    const data: TagData = { value: `${element.bullhorn}`, label: `${element.bullhorn}` };
    tagsList.value.push(data);
    // tagsArray is used to render the correct values for the new dropdown
    tagsArray.value.push(element.bullhorn);
  });
}

const causeList = ref<CauseData[]>([]);
const causeOptionsArray = ref<string[]>([]);
const causeLoading = ref(false);

async function getCausesList(query: any) {
  causeLoading.value = true;
  const benevityCauses = await getcausesAsDropdown(query);
  causeLoading.value = false;

  // Clear previous results when searching
  if (query) {
    causeOptionsArray.value = [];
    causeList.value = [];
  }

  benevityCauses.forEach((element: any) => {
    const data = {
      value: `${element.name} : ${element.id}`,
      label: `${element.name} : ${element.id}`,
    };

    if (!causeOptionsArray.value.includes(data.value)) {
      causeList.value.push(data);
      causeOptionsArray.value.push(data.value);
    }
  });
}

const onBoardOnStripe = async () => {
  if (!targetOrgId.value) {
    ElMessage.error('No organization selected');
    return;
  }
  try {
    const stripeLink = await doGetStripeAuthorizeUrl(targetOrgId.value);
    window.location.href = stripeLink;
  } catch (error) {
    console.error('Error getting Stripe authorization URL:', error);
    ElMessage.error('Failed to initiate Stripe connection');
  }
};

const decideIfEditAndFetch = async () => {
  if (route.params?.id) {
    isEditPage.value = true;
    await fetchSticker();
  }
};

const isActive = () => item.status === 'Active';

const handleClick = async () => {
  if (isUploading.value) return;

  hasAttemptedSubmit.value = true;

  if (showStripeWarning.value) {
    const message = stickerType.value === 'ticket'
      ? 'A connected Stripe account is required to save paid tickets. Please connect Stripe first.'
      : 'A connected Stripe account is required to save paid stickers. Please connect Stripe first.';
    ElMessage.error({
      message,
      duration: 5000,
    });
    return;
  }

  isUploading.value = true;

  try {
    // DISABLED: Allow sticker creation without onboarding checks
    // if (isOrgAdmin.value && selectedOrganization.value) {
    //   const organization = await getOrganization(selectedOrganization.value.id);

    //   // Check general onboarding first
    //   if (!organization.onboarded) {
    //     ElMessage.error({
    //       message: 'Organization must be fully onboarded before creating stickers.',
    //       duration: 3000,
    //     });
    //     return;
    //   }

    //   // Check if this is a paid sticker that requires Stripe onboarding
    //   const isPaidSticker = isPaidStickerType(stickerType.value, item);

    //   if (isPaidSticker && !organization.stripe_onboarded) {
    //     ElMessage.error({
    //       message: 'You need to onboard with Stripe before creating paid stickers.',
    //       duration: 3000,
    //     });
    //     return;
    //   }

    //   isStripeOnboarded.value = organization.stripe_onboarded;
    // }

    if (stickerType.value === 'sweepstakes' && item.status === 'Active' && isEditPage.value && isStickerSold.value) {
      await onDraw();
      await getWinnersList();
    } else {
      await onUpload();
    }
  } catch (error) {
    console.error('Error during upload:', error);
    let errorMessage = 'An error occurred during upload. Please try again.';
    if (error && error.errors && error.errors[0] && error.errors[0].message) {
      errorMessage = error.errors[0].message;
    } else if (error && error.message) {
      errorMessage = error.message;
    }
    ElMessage.error({
      message: errorMessage,
      duration: 3000,
    });
  } finally {
    isUploading.value = false;
  }
};

const onSaveBeforeStatusChange = async () => {
  // Save changes without showing validation errors or redirecting
  // This is called when Approve/Archive buttons are clicked
  try {
    if (stickerType.value === 'default') {
      const updatedItem = uploadDefaultStickerMap(item);
      await updateAdminOrOrgAdminSticker(updatedItem);
    } else if (stickerType.value === 'campaign') {
      const updatedItem = item;
      const cQuickCosts: number[] = [];
      const qc = item.quickCosts || [];
      qc.forEach((q) => {
        if (typeof q === 'number') {
          cQuickCosts.push(q);
        }
      });
      updatedItem.quickCosts = cQuickCosts;
      updatedItem.cost = parseFloat(parseFloat(updatedItem.cost).toFixed(2));
      if (Array.isArray(updatedItem.tags)) {
        updatedItem.tags = updatedItem.tags.join(' |=| ');
      } else if (typeof updatedItem.tags === 'string') {
        updatedItem.tags = updatedItem.tags.split(',').join(' |=| ');
      }
      if (Array.isArray(updatedItem.causes)) {
        updatedItem.causes = updatedItem.causes.join(' |=| ');
      } else if (typeof updatedItem.causes === 'string') {
        updatedItem.causes = updatedItem.causes.split(',').join(' |=| ');
      }
      item.campaignType = 'Campaign';
      updatedItem.varients = [];
      updatedItem.sweepstakesSticker = null;
      const stickerProps = getStickerProperties(updatedItem);
      await store.dispatch('sticker/SET_STICKER', stickerProps);
      await updateAdminOrOrgAdminSticker(stickerProps);
    } else if (stickerType.value === 'ticket') {
      const updatedItem = uploadTicketStickerMap(item);
      if (updatedItem.varients?.length) {
        updatedItem.cost = 0;
      }
      await store.dispatch('sticker/SET_STICKER', updatedItem);
      await updateAdminOrOrgAdminSticker(updatedItem);
    } else if (stickerType.value === 'sweepstakes') {
      const addItem = { ...item };
      addItem.cost = 0;
      addItem.tags = null;
      addItem.campaignType = 'Sweepstakes';
      addItem.isFeatured = false;
      addItem.IsCausesSearchAllowed = false;
      addItem.varients = [];
      if (addItem.sweepstakesSticker.numberOfPrizesAndWinners === null || addItem.sweepstakesSticker.numberOfPrizesAndWinners === '') {
        addItem.sweepstakesSticker.numberOfPrizesAndWinners = 1;
      }
      if (Array.isArray(item.causes)) {
        addItem.causes = item.causes.join(' |=| ');
      } else if (typeof item.causes === 'string') {
        addItem.causes = item.causes.split(',').join(' |=| ');
      } else {
        addItem.causes = '';
      }
      addItem.sweepstakesSticker.nonProfitBeneficiary = addItem.causes;
      const updatedItem = getSweepstakesStickerProperties(addItem);
      await store.dispatch('sticker/SET_STICKER', updatedItem);
      await updateAdminOrOrgAdminSticker(updatedItem);
    }
  } catch (error) {
    console.error('Error saving before status change:', error);
    throw error; // Re-throw to let BreadcrumbActions handle it
  }
};

onMounted(async () => {
  await store.dispatch('sticker/RESET_TO_DEFAULT'); // reset so the state does not persist from before
  await decideIfEditAndFetch();
  await getBullhornsAsTagsList();
  await getCausesList('');
  if (isEditPage.value) {
    await getStickerInsights();
    if (isStickerSold.value) {
      await getWinnersList();
    }
  }
});
</script>

<template>
  <article class="c-add-sticker">
    <BreadcrumbActions
      :is-edit-page="isEditPage"
      :sticker-type="stickerType"
      :isActive="isActive()"
      :is-admin="isAdmin"
      :fetch-sticker="fetchSticker"
      :on-save-before-status-change="onSaveBeforeStatusChange"
      :key="isEditPage.toString()"
    />
    <div v-if="isLoading" class="c-loading-spinner" />

    <div v-else-if="fetchError" class="c-fetch-error">
      <fai type="fal" icon="exclamation-circle" class="size-20 me-2" />
      <span>{{ fetchError }}</span>
      <el-button type="primary" size="small" class="ms-3" @click="fetchSticker">Retry</el-button>
    </div>

    <template v-else>
      <!-- Validation Errors at Top -->
      <section
        v-if="hasAttemptedSubmit && validationErrors.length"
        class="c-validation-alert c-validation-alert--top"
        role="alert"
      >
        <div class="c-validation-alert__header">
          <fai type="fal" icon="exclamation-circle" class="size-16 me-2" />
          <strong>Required fields missing:</strong>
        </div>
        <ul class="c-validation-alert__list">
          <li
            v-for="error in validationErrors"
            :key="error"
            class="c-validation-alert__item"
          >{{ error }}</li>
        </ul>
      </section>
      <section v-if="showStripeWarning" class="c-stripe-warning" role="alert">
        <div class="c-stripe-warning__header">
          <fai type="fal" icon="exclamation-triangle" class="size-16 me-2" />
          <strong>Stripe Connection Required</strong>
        </div>
        <div class="c-stripe-warning__text">
          This organization is not connected to Stripe. To publish or save paid tickets or paid stickers, please connect a Stripe account first.
        </div>
        <div class="c-stripe-warning__actions">
          <el-button type="primary" @click="onBoardOnStripe" class="has-fai">
            <fai type="fal" icon="external-link-alt" class="size-18 me-2" />
            <span>Onboard On Stripe</span>
          </el-button>
        </div>
      </section>

      <section class="c-add-sticker__container">
        <div class="c-add-sticker__card c-add-sticker__card--ticket">
          <section class="c-add-sticker__content">
            <article class="c-add-sticker__content__left">
              <StickerContent :onAddImage="onAddImage" :url="item.url" />
            </article>

            <article class="c-add-sticker__content__right">
              <!-- Ticket type: show title/slug/details here beside the image picker -->
              <div v-if="stickerType === 'ticket'" class="c-ticket-builder-top">
                <div class="c-ticket-builder-top__row">
                  <div class="c-ticket-builder-top__field">
                    <span class="c-ticket-builder-top__label">Event Title*</span>
                    <input
                      class="c-ticket-builder-top__input"
                      type="text"
                      placeholder="Title"
                      v-model="item.title"
                    />
                  </div>
                  <div class="c-ticket-builder-top__field">
                    <span class="c-ticket-builder-top__label">
                      Slug
                      <el-tooltip effect="light" :content="slugTip" placement="top" popper-class="max-w-350">
                        <fai type="fal" icon="info-circle" class="size-16 ms-2" />
                      </el-tooltip>
                    </span>
                    <input
                      class="c-ticket-builder-top__input"
                      type="text"
                      placeholder="your-event-name"
                      v-model="item.slug"
                      @input="sanitizeSlug"
                    />
                  </div>
                </div>
                <div class="c-ticket-builder-top__field c-ticket-builder-top__field--details">
                  <span class="c-ticket-builder-top__label">Details*</span>
                  <QuillEditor
                    :toolbar="['bold', 'italic', 'underline', 'strike']"
                    placeholder="Event description and details"
                    v-model:content="item.description"
                    content-type="html"
                    class="c-ticket-builder-top__quill"
                  />
                </div>
              </div>

              <!-- All other sticker types -->
              <DefaultSticker v-if="stickerType === 'default'" :availableTags="tagsArray" />
              <CampaignSticker
                v-if="stickerType === 'campaign'"
                :availableTags="tagsArray"
                :causes="causeOptionsArray"
                @onFetchCauses="getCausesList"
                :cause-loading="causeLoading"
              />
              <template v-if="stickerType === 'sweepstakes'">
                <SweepstakesSticker
                  :class="{ 'disable-and-opacity': isSweepstakeDisabled }"
                  :item="item"
                  :disabled="isSweepstakeDisabled"
                  :causes="causeOptionsArray"
                  @onFetchCauses="getCausesList"
                  :cause-loading="causeLoading"
                />
              </template>

              <article
                v-if="stickerType === 'sweepstakes'"
                class="s-sweepstakes-sticker__row s-sweepstakes-sticker__row--column"
                style="margin-top: 10px;"
              >
                <p>
                  By uploading you agree to our
                  <a
                    href="https://www.spotlight.xyz/legal-stuff/sweepstakes-rules#age-restrictions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Age Restrictions
                  </a>,
                  <a
                    href="https://www.spotlight.xyz/legal-stuff/sweepstakes-rules#eligible-countries"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Eligible Countries
                  </a>,
                  <a
                    href="https://www.spotlight.xyz/legal-stuff/sweepstakes-rules#donation-transparency"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Donation Transparency
                  </a>,
                  <a
                    href="https://www.spotlight.xyz/legal-stuff/sweepstakes-rules#selection-process"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Selection Process
                  </a>, and
                  <a
                    href="https://www.spotlight.xyz/legal-stuff/sweepstakes-rules#disclaimer"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Disclaimer
                  </a>.
                </p>
              </article>

              <!-- Submit / Save button — not shown for ticket (TicketSticker has its own actions) -->
              <template v-if="stickerType !== 'ticket'">
                <div class="c-add-sticker__actions">
                  <!-- UPLOAD TO SPOTLIGHT CONTAINER -->
                  <template v-if="!isEditPage">
                    <button
                      class="c-add-sticker__btn c-add-sticker__btn--primary"
                      type="button"
                      :disabled="isUploading"
                      @click="handleClick"
                    >
                      <fai class="size-14" icon="upload" type="fas" v-if="!isUploading" />
                      {{ isUploading ? 'Uploading...' : 'Upload to Spotlight' }}
                    </button>
                  </template>

                  <!-- SAVE STICKER OR REDRAW BUTTON -->
                  <template v-else>
                    <button
                      v-if="stickerType !== 'sweepstakes'
                        || (stickerType === 'sweepstakes' && !isStickerSold)"
                      class="c-add-sticker__btn c-add-sticker__btn--primary"
                      type="button"
                      :disabled="isUploading"
                      @click="handleClick"
                    >
                      <fai class="size-14" icon="floppy-disk" type="fas" />
                      {{ isUploading ? 'Saving...' : 'Save Sticker' }}
                    </button>

                    <div
                      v-if="(stickerType === 'sweepstakes'
                        && (item.status === 'Active' || item.status === 'Archived')
                        && isStickerSold && !item.isWinnerConfirmed)"
                      class="redraw-container"
                      style="position: relative;"
                    >
                      <button
                        class="c-add-sticker__btn c-add-sticker__btn--secondary"
                        type="button"
                        :disabled="winners === null"
                        @click="handleButtonClick"
                      >
                        <fai class="size-14" icon="repeat" type="fas" />
                        Redraw Winners
                      </button>
                      <div
                        v-if="isTextVisible"
                        class="c-add-sticker__winners-list"
                      >
                        <button type="button" class="close-btn" @click="handleClose">X</button>
                        <p style="font-weight: bold;">Choose who to redraw for:</p>
                        <ul style="list-style: none; padding-left: 0;">
                          <li
                            v-for="winner in winners"
                            :key="winner.id"
                            class="winner-item"
                            style="display: flex; align-items: center; justify-content: space-between;"
                          >
                            <div style="display: flex; align-items: center;">
                              <img
                                v-if="winner.participantAvatar"
                                :src="winner.participantAvatar"
                                alt="Winner Avatar"
                                style="width: 20px; height: 20px; margin-right: 8px; border-radius: 50%;" />
                              <fai
                                v-else
                                class="icon-size-14"
                                type="fas"
                                icon="user-circle"
                                style="width: 20px; height: 20px; margin-right: 8px;" />
                              <label :for="winner.id">{{ winner.participantName }}</label>
                            </div>
                            <el-checkbox
                              v-model="selectedWinners"
                              :id="winner.id"
                              :value="winner"
                              class="c-quick-costs" />
                          </li>
                        </ul>
                      </div>
                    </div>

                    <button
                      v-if="stickerType === 'sweepstakes'
                        && (item.status === 'Active' || item.status === 'Archived')
                        && isStickerSold && !item.isWinnerConfirmed"
                      class="c-add-sticker__btn c-add-sticker__btn--primary"
                      type="button"
                      :disabled="winners === null"
                      @click="confirmWinners"
                    >
                      Confirm Winners
                    </button>
                  </template>
                </div>
              </template>
            </article>
          </section>

          <!-- Full-width step indicator + tabs + actions (ticket type only, nested inside the same card) -->
          <section v-if="stickerType === 'ticket'" class="c-add-sticker__ticket-steps">
            <TicketSticker
              :availableTags="tagsArray"
              :causes="causeOptionsArray"
              :is-uploading="isUploading"
              :show-errors="hasAttemptedSubmit"
              :validation-errors="validationErrors"
              @onFetchCauses="getCausesList"
              @saveDraft="onSaveDraft"
              @publishEvent="onPublishEvent"
              :cause-loading="causeLoading"
            />
          </section>
        </div>

        <section
          class="c-add-sticker__extra"
          :class="{ 'disabled-section': stickerType === 'sweepstakes' && isSweepstakeDisabled }"
        >
          <article class="c-add-sticker__extra__type">
            <span class="c-add-sticker__extra__title mb-1">Sticker Type</span>
            <el-select
              v-model="stickerType"
              class="c-input c-input__select"
              :disabled="stickerType === 'sweepstakes' && isSweepstakeDisabled"
            >
              <el-option
                v-if="!(isEditPage && stickerType === 'sweepstakes')"
                label="Default"
                value="default"
              >
                Default
              </el-option>
              <el-option
                v-if="!(isEditPage && stickerType === 'sweepstakes')"
                label="Campaign"
                value="campaign"
              >
                Campaign
              </el-option>
              <el-option
                v-if="!(isEditPage && stickerType === 'sweepstakes')"
                label="Ticket"
                value="ticket"
              >
                Ticket
              </el-option>
              <el-option
                v-if="!(isEditPage && stickerType !== 'sweepstakes')"
                label="Sweepstakes"
                value="sweepstakes"
              >
                Sweepstakes
              </el-option>
            </el-select>
          </article>
          <Insights class="c-add-sticker__extra__type" v-if="isEditPage" />

          <section
            class="c-add-sticker__extra__type c-add-sticker__qr-code"
            v-if="isEditPage && item && item.id"
          >
            <span class="c-add-sticker__extra__title mb-2">Share Sticker</span>
            <qrcode-vue :value="stickerUrl" :size="167" />
            <a :href="stickerUrl" target="_blank" rel="noopener noreferrer">{{ stickerUrl }}</a>
          </section>

          <section
            class="c-add-sticker__extra__type c-add-sticker__deep-link"
            v-if="isEditPage && item && item.id"
          >
            <span class="c-add-sticker__extra__title mb-2">In-App Sticker Link</span>
            <div class="c-add-sticker__deep-link-group">
              <el-input :value="stickerDeepLink" readonly size="small" />
              <el-button type="primary" size="small" @click="copyStickerDeepLink">
                <span>Copy</span>
              </el-button>
            </div>
          </section>

        </section>
      </section>
    </template>
  </article>
</template>

<style lang="scss" src="./AddSticker.scss" />
